import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DropdownMenu } from "../components/DropdownMenu";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import { useIsMobile } from "../hooks/useIsMobile";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import {
  getVendorBills, getVendorBill, getVendors, createVendorBill,
  submitVendorBill, approveVendorBill, bulkApproveVendorBills, payVendorBill, voidVendorBill,
  downloadVendorBillReceipt, getTenantCurrency, getVendorBillVatSummary, downloadVendorBillWhtCertificate, downloadPp30Export, downloadPndExport,
  ocrVendorBillReceipt, getUpcomingRecurringBills, downloadVendorBillsExport, downloadVendorBillsPdf,
  type VendorBillListResponse,
} from "../api";

const NAVY = "#0f172a";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  draft:     { label: "Draft",     bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
  submitted: { label: "Submitted", bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  approved:  { label: "Approved",  bg: "#dcfce7", text: "#166534", border: "#86efac" },
  paid:      { label: "Paid",      bg: "#eff6ff", text: "#1d4ed8", border: "#93c5fd" },
  void:      { label: "Void",      bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
};

const WHT_CATEGORIES = [
  { value: "professional_services", label: "Professional Services", rate: "3" },
  { value: "advertising", label: "Advertising", rate: "2" },
  { value: "rent", label: "Rent", rate: "5" },
  { value: "transport", label: "Transport", rate: "1" },
  { value: "other", label: "Other", rate: "0" },
];

const BLANK_ITEM = { description: "", qty: "1", unit_price: "", amount: "" };

const RECURRENCE_INTERVALS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const BLANK_FORM = {
  vendor_id: "", bill_date: new Date().toISOString().split("T")[0], due_date: "",
  currency: "USD", currency_symbol: "$",
  line_items: [{ ...BLANK_ITEM }],
  discount: "0",
  vat_rate: "7", is_vat_claimable: true,
  wht_category: "", wht_rate: "0",
  notes: "",
  is_recurring: false, recurrence_interval: "monthly", recurrence_end_date: "",
};

export default function VendorBillsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { downloading, download } = useBlockingDownload();

  const [statusFilter, setStatusFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [payModal, setPayModal] = useState<{ id: number } | null>(null);
  const [payForm, setPayForm] = useState({ payment_method: "", payment_reference: "" });
  const [showVatDrilldown, setShowVatDrilldown] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  const [selectedBillIds, setSelectedBillIds] = useState<Set<number>>(new Set());
  const [bulkFailures, setBulkFailures] = useState<Record<number, string>>({});
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const isMobile = useIsMobile(768);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const currentPeriod = new Date().toISOString().slice(0, 7); // YYYY-MM

  // Table view: search/filter/sort/pagination (spec: vendor-bills-table-ui), mirroring the
  // pattern already shipped for AI Expense Capture.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [vendorFilterId, setVendorFilterId] = useState<number | "">("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [sortBy, setSortBy] = useState("");   // "" = default (bill date desc)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [vendorFilterId !== "" ? "x" : "", dateFromFilter, dateToFilter].filter(Boolean).length;

  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // A narrowed search/filter can leave the current page past the new last page — land back
  // on page 1 rather than showing an empty page (same rationale as expense-capture-table-view).
  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, vendorFilterId, dateFromFilter, dateToFilter, currencyFilter, search, sortBy, sortDir]);

  const { data: listResp, isLoading, isError } = useQuery({
    queryKey: ["vendor-bills", statusFilter, vendorFilterId, dateFromFilter, dateToFilter, currencyFilter, search, sortBy, sortDir, currentPage],
    queryFn: () => getVendorBills({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(vendorFilterId !== "" ? { vendor_id: vendorFilterId as number } : {}),
      ...(dateFromFilter ? { date_from: dateFromFilter } : {}),
      ...(dateToFilter ? { date_to: dateToFilter } : {}),
      ...(currencyFilter ? { currency: currencyFilter } : {}),
      ...(search ? { search } : {}),
      ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
      page: currentPage, page_size: PAGE_SIZE,
    }),
  });
  const bills: any[] = (listResp as VendorBillListResponse | undefined)?.items ?? [];
  const totalCount: number = (listResp as VendorBillListResponse | undefined)?.total ?? 0;
  const totalPages: number = (listResp as VendorBillListResponse | undefined)?.total_pages ?? 1;
  // Distinct-per-tenant, not derived from the current page — with server-side pagination a
  // given page might only ever show one currency even though the tenant has several, so this
  // has to come from the backend rather than being computed from `bills` (previously safe
  // when the endpoint returned every bill, unpaginated).
  const currencies: string[] = (listResp as VendorBillListResponse | undefined)?.currencies ?? [];
  React.useEffect(() => {
    if (currencyFilter && !currencies.includes(currencyFilter)) setCurrencyFilter("");
  }, [currencies, currencyFilter]);

  function toggleSort(column: string) {
    if (sortBy === column) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(column); setSortDir("asc"); }
  }
  const { data: vatSummary } = useQuery({
    queryKey: ["vendor-bills-vat-summary", currentPeriod],
    queryFn: () => getVendorBillVatSummary(currentPeriod),
  });
  const { data: upcomingRecurring = [] } = useQuery({
    queryKey: ["vendor-bills-upcoming-recurring"],
    queryFn: () => getUpcomingRecurringBills(30),
  });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors", false], queryFn: () => getVendors() });
  const { data: viewBill } = useQuery({
    queryKey: ["vendor-bill", viewingId],
    queryFn: () => getVendorBill(viewingId!),
    enabled: viewingId !== null,
  });
  const { data: currencyData } = useQuery({ queryKey: ["tenant-currency"], queryFn: getTenantCurrency });
  React.useEffect(() => {
    if (currencyData) setForm(f => ({ ...f, currency: (currencyData as any).currency || "USD", currency_symbol: (currencyData as any).currency_symbol || "$" }));
  }, [currencyData]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["vendor-bills"] }); qc.invalidateQueries({ queryKey: ["vendor-bill"] }); };

  const createMut = useMutation({
    mutationFn: createVendorBill,
    onSuccess: () => { invalidate(); closeForm(); },
  });
  const submitMut = useMutation({ mutationFn: submitVendorBill, onSuccess: invalidate });
  const approveMut = useMutation({ mutationFn: approveVendorBill, onSuccess: invalidate });
  const voidMut = useMutation({ mutationFn: voidVendorBill, onSuccess: invalidate });
  const bulkApproveMut = useMutation({
    mutationFn: bulkApproveVendorBills,
    onSuccess: (result: { approved: number[]; failed: { id: number; reason: string; message: string }[] }) => {
      invalidate();
      setSelectedBillIds(new Set());
      const failureMap: Record<number, string> = {};
      result.failed.forEach(f => { failureMap[f.id] = f.message; });
      setBulkFailures(failureMap);
      if (result.failed.length === 0) {
        toast(`${result.approved.length} bill${result.approved.length !== 1 ? "s" : ""} approved`, "success");
      } else if (result.approved.length === 0) {
        toast(`All ${result.failed.length} approvals failed — see highlighted rows for reasons`, "error");
      } else {
        toast(`${result.approved.length} approved, ${result.failed.length} failed — see highlighted rows`, "info");
      }
    },
  });
  const payMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { payment_method: string; payment_reference: string } }) => payVendorBill(id, data),
    onSuccess: () => { invalidate(); setPayModal(null); setPayForm({ payment_method: "", payment_reference: "" }); },
  });

  function closeForm() {
    setShowForm(false);
    setForm(BLANK_FORM);
    setReceiptFile(null);
    setAiFilledFields(new Set());
  }

  function addLineItem() {
    setForm(f => ({ ...f, line_items: [...f.line_items, { ...BLANK_ITEM }] }));
  }
  function removeLineItem(i: number) {
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  }
  function clearAiMark(field: string) {
    setAiFilledFields(prev => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  }
  function updateLineItem(i: number, field: string, value: string) {
    clearAiMark("line_items");
    setForm(f => {
      const items = f.line_items.map((item, idx) => {
        if (idx !== i) return item;
        const updated = { ...item, [field]: value };
        if (field === "qty" || field === "unit_price") {
          const qty = parseFloat(field === "qty" ? value : updated.qty) || 0;
          const price = parseFloat(field === "unit_price" ? value : updated.unit_price) || 0;
          updated.amount = (qty * price).toFixed(2);
        }
        return updated;
      });
      return { ...f, line_items: items };
    });
  }

  function selectVendor(vendorId: string) {
    clearAiMark("vendor_id");
    const vendor = (vendors as any[]).find(v => String(v.id) === vendorId);
    setForm(f => {
      const cat = WHT_CATEGORIES.find(c => c.value === vendor?.default_wht_category);
      return { ...f, vendor_id: vendorId, wht_category: vendor?.default_wht_category || f.wht_category, wht_rate: cat?.rate || f.wht_rate };
    });
  }

  function toggleBillSelected(id: number) {
    setSelectedBillIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleScanReceipt(file: File) {
    setShowForm(true);
    setScanningReceipt(true);
    let result: any;
    try {
      result = await ocrVendorBillReceipt(file);
    } catch {
      toast("Could not read receipt — please fill in manually", "error");
      return;
    } finally {
      setScanningReceipt(false);
    }
    const filled = new Set<string>();

    setForm(f => {
      const next = { ...f };
      if (result.matched_vendor_id) { next.vendor_id = String(result.matched_vendor_id); filled.add("vendor_id"); }
      if (result.bill_date) { next.bill_date = result.bill_date; filled.add("bill_date"); }
      if (result.due_date) { next.due_date = result.due_date; filled.add("due_date"); }
      if (Array.isArray(result.line_items) && result.line_items.length > 0) {
        next.line_items = result.line_items.map((item: any) => {
          const qty = parseFloat(item.qty) || 1;
          const price = parseFloat(item.unit_price) || 0;
          return { description: item.description || "", qty: String(qty), unit_price: String(price), amount: (qty * price).toFixed(2) };
        });
        filled.add("line_items");
      }
      if (result.discount != null) { next.discount = String(result.discount); filled.add("discount"); }
      if (result.vat_rate != null) { next.vat_rate = String(result.vat_rate); filled.add("vat_rate"); }
      if (result.wht_rate != null) { next.wht_rate = String(result.wht_rate); filled.add("wht_rate"); }
      if (result.currency) { next.currency = result.currency; filled.add("currency"); }
      return next;
    });

    setAiFilledFields(filled);
    if (!result.matched_vendor_id && result.vendor_name) {
      toast(`Extracted vendor "${result.vendor_name}" — no matching vendor found, please select or create one`, "info");
    } else {
      toast("Receipt scanned — please review the highlighted fields before saving", "success");
    }
  }

  const calcSubtotal = () => form.line_items.reduce((s, item) => s + (parseFloat(item.amount) || 0), 0);
  const calcDiscount = () => parseFloat(form.discount) || 0;
  const calcDiscountedSubtotal = () => Math.max(calcSubtotal() - calcDiscount(), 0);
  const calcVat = () => calcDiscountedSubtotal() * (parseFloat(form.vat_rate) || 0) / 100;
  const calcWht = () => calcDiscountedSubtotal() * (parseFloat(form.wht_rate) || 0) / 100;
  const calcTotal = () => calcDiscountedSubtotal() + calcVat();
  const calcNetPayable = () => calcTotal() - calcWht();

  function handleSubmit() {
    if (!form.vendor_id || !form.bill_date || form.line_items.some(i => !i.description || !i.amount)) return;
    const fd = new FormData();
    fd.append("vendor_id", form.vendor_id);
    fd.append("bill_date", form.bill_date);
    if (form.due_date) fd.append("due_date", form.due_date);
    fd.append("currency", form.currency);
    fd.append("currency_symbol", form.currency_symbol);
    fd.append("line_items", JSON.stringify(form.line_items.map(i => ({
      description: i.description, qty: parseFloat(i.qty) || 1,
      unit_price: parseFloat(i.unit_price) || 0, amount: parseFloat(i.amount) || 0,
    }))));
    fd.append("discount", form.discount);
    fd.append("vat_rate", form.vat_rate);
    fd.append("is_vat_claimable", String(form.is_vat_claimable));
    if (form.wht_category) fd.append("wht_category", form.wht_category);
    fd.append("wht_rate", form.wht_rate);
    fd.append("notes", form.notes);
    fd.append("is_recurring", String(form.is_recurring));
    if (form.is_recurring) {
      fd.append("recurrence_interval", form.recurrence_interval);
      if (form.recurrence_end_date) fd.append("recurrence_end_date", form.recurrence_end_date);
    }
    if (receiptFile) fd.append("receipt", receiptFile);
    createMut.mutate(fd);
  }

  async function handleVoid(id: number) {
    if (await confirm("Void this bill? This cannot be undone.", { confirmLabel: "Void" })) voidMut.mutate(id);
  }

  async function handleDownloadReceipt(id: number) {
    await download(() => downloadVendorBillReceipt(id), "Failed to download receipt");
  }

  async function handleDownloadWhtCertificate(id: number) {
    await download(() => downloadVendorBillWhtCertificate(id), "Failed to download WHT certificate");
  }

  async function handleExportPp30() {
    await download(() => downloadPp30Export(currentPeriod), "Failed to export PP.30");
  }

  async function handleExportPnd(formType: "PND3" | "PND53") {
    await download(() => downloadPndExport(currentPeriod, formType), `Failed to export ${formType}`);
  }

  // CSV/PDF export respect whatever status/vendor/date-range filters are active on screen
  // (spec: vendor-bills-table-ui AC9/AC10) rather than always exporting the current period.
  function activeExportFilters() {
    return {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(vendorFilterId !== "" ? { vendor_id: vendorFilterId as number } : {}),
      ...(dateFromFilter ? { date_from: dateFromFilter } : {}),
      ...(dateToFilter ? { date_to: dateToFilter } : {}),
      ...(currencyFilter ? { currency: currencyFilter } : {}),
    };
  }

  async function handleExportCsv() {
    await download(() => downloadVendorBillsExport(activeExportFilters()), "Failed to export CSV");
  }

  async function handleExportPdf() {
    await download(() => downloadVendorBillsPdf(activeExportFilters()), "Failed to export PDF");
  }

  const today = new Date().toISOString().split("T")[0];

  function aiStyle(field: string): React.CSSProperties {
    if (!aiFilledFields.has(field)) return inputStyle;
    return { ...inputStyle, background: "#fefce8", borderColor: "#fde047" };
  }

  function SortHeader({ column, label, align }: { column: string; label: string; align?: "right" }) {
    const active = sortBy === column;
    return (
      <th onClick={() => toggleSort(column)} style={{
        padding: "10px 12px", textAlign: align || "left", fontSize: 12, fontWeight: 600,
        color: active ? NAVY : "#64748b", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
      }}>
        {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  }

  // Every per-row action available on the card view stays available per row (spec AC7); a
  // bulk-approve failure gets its own full-width sub-row so it doesn't distort row height.
  function renderBillTableRow(b: any) {
    const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.draft;
    const isOverdue = b.due_date && b.due_date < today && !["paid", "void"].includes(b.status);
    return (
      <React.Fragment key={b.id}>
        <tr style={{ background: bulkFailures[b.id] ? "#fef2f2" : "white" }}>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
            {b.status === "submitted" && (
              <input type="checkbox" checked={selectedBillIds.has(b.id)}
                onChange={() => toggleBillSelected(b.id)}
                aria-label={`Select ${b.vendor_name || "bill"} for bulk action`}
                style={{ width: 16, height: 16, cursor: "pointer", verticalAlign: "middle", marginRight: 8 }} />
            )}
            <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b", cursor: "pointer" }} onClick={() => setViewingId(b.id)}>
              {b.bill_number}
            </span>
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, cursor: "pointer" }} onClick={() => setViewingId(b.id)}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, color: "#475569" }}>{b.vendor_name}</span>
              {b.is_recurring && (
                <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>
                  ↻ Recurring
                </span>
              )}
            </div>
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                {sc.label}
              </span>
              {isOverdue && (
                <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                  Overdue
                </span>
              )}
            </div>
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, textAlign: "right", fontWeight: 700, color: NAVY, whiteSpace: "nowrap" }}>
            {b.currency_symbol}{(b.net_payable || 0).toLocaleString("en", { minimumFractionDigits: 2 })}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, whiteSpace: "nowrap" }}>
            {formatDate(b.bill_date)}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, whiteSpace: "nowrap", color: isOverdue ? "#dc2626" : undefined, fontWeight: isOverdue ? 600 : undefined }}>
            {b.due_date ? formatDate(b.due_date) : "—"}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {b.status === "draft" && (
                <AsyncButton onClick={() => submitMut.mutateAsync(b.id)} style={outlineBtnStyle}>Submit</AsyncButton>
              )}
              {b.status === "submitted" && (
                <AsyncButton onClick={() => approveMut.mutateAsync(b.id)} style={{ ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac" }}>Approve</AsyncButton>
              )}
              {b.status === "approved" && (
                <button onClick={() => setPayModal({ id: b.id })} style={{ ...outlineBtnStyle, color: "#1d4ed8", borderColor: "#93c5fd" }}>Mark Paid</button>
              )}
              {!["paid", "void"].includes(b.status) && (
                <AsyncButton onClick={() => handleVoid(b.id)} style={{ ...outlineBtnStyle, color: "#dc2626" }}>Void</AsyncButton>
              )}
              {b.status === "paid" && b.wht_amount > 0 && (
                <AsyncButton onClick={() => handleDownloadWhtCertificate(b.id)} style={outlineBtnStyle}>WHT Cert</AsyncButton>
              )}
            </div>
          </td>
        </tr>
        {bulkFailures[b.id] && (
          <tr style={{ background: "#fef2f2" }}>
            <td colSpan={7} style={{ padding: "0 12px 10px", fontSize: 12, color: "#dc2626" }}>⚠ {bulkFailures[b.id]}</td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  // Mobile stacked layout (below the isMobile breakpoint) — same data/state/actions as the
  // desktop table, just a different presentation (spec AC11). This is the same markup the
  // card list used before the table view — kept verbatim, just extracted into a function.
  function renderBillCard(b: any) {
    const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.draft;
    const isOverdue = b.due_date && b.due_date < today && !["paid", "void"].includes(b.status);
    return (
      <div key={b.id} className="vendor-card-row" style={{
        background: "white", border: bulkFailures[b.id] ? "1px solid #fca5a5" : "1px solid #e2e8f0", borderRadius: 12,
        padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        {b.status === "submitted" && (
          <input type="checkbox" checked={selectedBillIds.has(b.id)}
            onClick={e => e.stopPropagation()}
            onChange={() => toggleBillSelected(b.id)}
            style={{ marginRight: 14, flexShrink: 0, width: 16, height: 16, cursor: "pointer" }} />
        )}
        <div style={{ flex: 1, cursor: "pointer" }} onClick={() => setViewingId(b.id)}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{b.bill_number}</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: "#475569" }}>{b.vendor_name}</span>
            <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
              {sc.label}
            </span>
            {isOverdue && (
              <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                Overdue
              </span>
            )}
            {b.is_recurring && (
              <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                ↻ Recurring
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#64748b", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>
              {b.currency_symbol}{(b.net_payable || 0).toLocaleString("en", { minimumFractionDigits: 2 })}
            </span>
            <span>Bill date: {formatDate(b.bill_date)}</span>
            {b.due_date && <span style={isOverdue ? { color: "#dc2626", fontWeight: 600 } : {}}>Due: {formatDate(b.due_date)}</span>}
          </div>
          {bulkFailures[b.id] && (
            <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>⚠ {bulkFailures[b.id]}</div>
          )}
        </div>
        <div className="vendor-card-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {b.status === "draft" && (
            <AsyncButton onClick={() => submitMut.mutateAsync(b.id)} style={outlineBtnStyle}>Submit</AsyncButton>
          )}
          {b.status === "submitted" && (
            <AsyncButton onClick={() => approveMut.mutateAsync(b.id)} style={{ ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac" }}>Approve</AsyncButton>
          )}
          {b.status === "approved" && (
            <button onClick={() => setPayModal({ id: b.id })} style={{ ...outlineBtnStyle, color: "#1d4ed8", borderColor: "#93c5fd" }}>Mark Paid</button>
          )}
          {!["paid", "void"].includes(b.status) && (
            <AsyncButton onClick={() => handleVoid(b.id)} style={{ ...outlineBtnStyle, color: "#dc2626" }}>Void</AsyncButton>
          )}
          {b.status === "paid" && b.wht_amount > 0 && (
            <AsyncButton onClick={() => handleDownloadWhtCertificate(b.id)} style={outlineBtnStyle}>WHT Certificate</AsyncButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <LoadingOverlay active={
        createMut.isPending || submitMut.isPending || approveMut.isPending || voidMut.isPending ||
        bulkApproveMut.isPending || payMut.isPending || downloading
      } />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        {...(isMobile ? { capture: "environment" as const } : {})}
        style={{ display: "none" }}
        onChange={async e => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) await handleScanReceipt(file);
        }}
      />

      <div className="page-header">
        <div>
          <h1>Vendor Bills</h1>
          <p>Accounts payable — bills received from vendors</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <DropdownMenu label="Export ▾" style={outlineBtnStyle}
            items={[
              { label: "Export CSV", onClick: handleExportCsv },
              { label: "Export PDF", onClick: handleExportPdf },
            ]} />
          <AsyncButton onClick={() => handleExportPp30()} style={outlineBtnStyle}>Export PP.30</AsyncButton>
          <AsyncButton onClick={() => handleExportPnd("PND3")} style={outlineBtnStyle}>Export PND.3</AsyncButton>
          <AsyncButton onClick={() => handleExportPnd("PND53")} style={outlineBtnStyle}>Export PND.53</AsyncButton>
          <a href="/vendor-bills/bulk-upload" style={{ ...outlineBtnStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            Bulk Upload
          </a>
          {!isMobile && (
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={scanningReceipt}
              style={{ ...outlineBtnStyle, opacity: scanningReceipt ? 0.6 : 1, cursor: scanningReceipt ? "not-allowed" : "pointer" }}
            >
              {scanningReceipt ? "Scanning…" : "📷 Scan Receipt"}
            </button>
          )}
          <button onClick={() => { showForm ? closeForm() : setShowForm(true); }} style={primaryBtnStyle}>
            {showForm ? "Cancel" : "+ New Bill"}
          </button>
        </div>
      </div>

      {isMobile && (
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={scanningReceipt}
          aria-label="Scan Receipt"
          style={{
            position: "fixed", bottom: 24, right: 20, width: 56, height: 56, borderRadius: "50%",
            background: NAVY, color: "white", border: "none", boxShadow: "0 4px 14px rgba(15, 23, 42, 0.35)",
            fontSize: 24, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: scanningReceipt ? "not-allowed" : "pointer", opacity: scanningReceipt ? 0.6 : 1,
            zIndex: 40,
          }}
        >
          {scanningReceipt ? "…" : "📷"}
        </button>
      )}

      {vatSummary && (vatSummary as any).claimable_count > 0 && (
        <div
          onClick={() => setShowVatDrilldown(true)}
          style={{
            background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
          <div>
            <div style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>CLAIMABLE INPUT VAT — THIS MONTH</div>
            {(vatSummary as any).claimable_vat_by_currency.map((g: any) => (
              <div key={g.currency} style={{ fontSize: 20, fontWeight: 700, color: "#166534" }}>
                {g.currency_symbol}{g.total_vat.toLocaleString("en", { minimumFractionDigits: 2 })}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
            {(vatSummary as any).claimable_count} bill{(vatSummary as any).claimable_count !== 1 ? "s" : ""} — View details →
          </div>
        </div>
      )}

      {(upcomingRecurring as any[]).length > 0 && (
        <div style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#1d4ed8", fontWeight: 600, marginBottom: 8 }}>UPCOMING RECURRING BILLS — NEXT 30 DAYS</div>
          <div style={{ display: "grid", gap: 6 }}>
            {(upcomingRecurring as any[]).map(b => (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#1e3a8a" }}>
                <span>{b.vendor_name} — {b.bill_number}</span>
                <span style={{ fontWeight: 600 }}>Next: {formatDate(b.recurrence_next_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div className="filter-bar" style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["", "draft", "submitted", "approved", "paid", "void"].map(s => (
              <button key={s || "all"} onClick={() => setStatusFilter(s)} style={{
                padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer",
                fontSize: 13, fontWeight: 500,
                background: statusFilter === s ? NAVY : "white",
                color: statusFilter === s ? "white" : "#64748b",
              }}>{s ? STATUS_CONFIG[s]?.label : "All"}</button>
            ))}
          </div>
          {selectedBillIds.size > 0 && (
            <AsyncButton
              onClick={() => bulkApproveMut.mutateAsync(Array.from(selectedBillIds))}
              style={{ ...primaryBtnStyle, background: "#16a34a" }}>
              Approve Selected ({selectedBillIds.size})
            </AsyncButton>
          )}
        </div>
        {currencies.length > 1 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>Currency:</span>
            {["", ...currencies].map(c => (
              <button key={c || "all-currencies"} onClick={() => setCurrencyFilter(c)} style={{
                padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer",
                fontSize: 13, fontWeight: 500,
                background: currencyFilter === c ? NAVY : "white",
                color: currencyFilter === c ? "white" : "#64748b",
              }}>{c || "All"}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: showFilters || !isMobile ? 8 : 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={labelStyle}>Search</label>
          <input type="text" placeholder="Bill #, vendor, notes, line items…" value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ ...inputStyle, width: 220 }} />
        </div>
        {/* Vendor/date/sort collapse behind this on mobile — search stays visible either way
            so narrowing results doesn't need an extra tap (same pattern as expense-capture-table-view AC9). */}
        {isMobile && (
          <button onClick={() => setShowFilters(s => !s)} style={{ ...outlineBtnStyle, minHeight: 34, fontWeight: activeFilterCount > 0 ? 700 : 400 }}>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        )}
      </div>

      {(!isMobile || showFilters) && (
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={labelStyle}>Vendor</label>
          <select value={vendorFilterId} onChange={e => setVendorFilterId(e.target.value ? Number(e.target.value) : "")} style={{ ...inputStyle, width: 160 }}>
            <option value="">All vendors</option>
            {(vendors as any[]).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <DateInput value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} style={{ ...inputStyle, width: 140 }} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <DateInput value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} style={{ ...inputStyle, width: 140 }} />
        </div>
        <div>
          <label style={labelStyle}>Sort by</label>
          {/* Always visible on both layouts — the desktop table's clickable column headers
              set the same sortBy/sortDir state, but mobile has no header row to click. */}
          <div style={{ display: "flex", gap: 4 }}>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inputStyle, width: 130 }}>
              <option value="">Default</option>
              <option value="bill_date">Bill Date</option>
              <option value="due_date">Due Date</option>
              <option value="net_payable">Net Payable</option>
              <option value="vendor">Vendor</option>
              <option value="status">Status</option>
            </select>
            <button onClick={() => setSortDir(d => (d === "asc" ? "desc" : "asc"))}
              title={sortDir === "asc" ? "Ascending" : "Descending"}
              style={{ ...outlineBtnStyle, minHeight: 34, padding: "6px 10px" }}>
              {sortDir === "asc" ? "▲" : "▼"}
            </button>
          </div>
        </div>
        {(searchInput || vendorFilterId !== "" || dateFromFilter || dateToFilter) && (
          <button onClick={() => { setSearchInput(""); setVendorFilterId(""); setDateFromFilter(""); setDateToFilter(""); }}
            style={{ ...outlineBtnStyle, minHeight: 34 }}>
            Clear filters
          </button>
        )}
      </div>
      )}

      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>New Vendor Bill</h3>
            <label style={{ ...outlineBtnStyle, cursor: "pointer", display: "inline-block" }}>
              Scan Receipt
              <input type="file" accept="image/jpeg,image/png,.pdf" style={{ display: "none" }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) await handleScanReceipt(file);
                }} />
            </label>
          </div>
          {aiFilledFields.size > 0 && (
            <div style={{ background: "#fefce8", border: "1px solid #fde047", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "#854d0e" }}>
              Fields highlighted in yellow were filled by AI from your scanned receipt — please verify before saving.
            </div>
          )}
          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Vendor *</label>
              <select value={form.vendor_id} onChange={e => selectVendor(e.target.value)} style={aiStyle("vendor_id")}>
                <option value="">Select vendor…</option>
                {(vendors as any[]).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Bill Date *</label>
              <DateInput value={form.bill_date} onChange={e => { clearAiMark("bill_date"); setForm(f => ({ ...f, bill_date: e.target.value })); }} style={aiStyle("bill_date")} />
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <DateInput value={form.due_date} onChange={e => { clearAiMark("due_date"); setForm(f => ({ ...f, due_date: e.target.value })); }} style={aiStyle("due_date")} />
            </div>
            <div>
              <label style={labelStyle}>Receipt / Supporting Document</label>
              <input type="file" accept="image/*,.pdf" onChange={e => setReceiptFile(e.target.files?.[0] || null)} style={{ fontSize: 13 }} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: "#475569" }}>Line Items *</label>
              <button type="button" onClick={addLineItem} style={outlineBtnStyle}>+ Add Item</button>
            </div>
            <div className="table-wrap">
            {form.line_items.map((item, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1.5fr 1.5fr auto", gap: 8, marginBottom: 8, minWidth: 480 }}>
                <input placeholder="Description" value={item.description}
                  onChange={e => updateLineItem(i, "description", e.target.value)} style={aiStyle("line_items")} />
                <input type="number" placeholder="Qty" value={item.qty} min={0} step="0.01"
                  onChange={e => updateLineItem(i, "qty", e.target.value)} style={aiStyle("line_items")} />
                <input type="number" placeholder="Unit Price" value={item.unit_price} min={0} step="0.01"
                  onChange={e => updateLineItem(i, "unit_price", e.target.value)} style={aiStyle("line_items")} />
                <input type="number" placeholder="Amount" value={item.amount} min={0} step="0.01"
                  onChange={e => updateLineItem(i, "amount", e.target.value)} style={aiStyle("line_items")} />
                <button type="button" onClick={() => removeLineItem(i)}
                  style={{ background: "none", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: 6, cursor: "pointer", padding: "0 10px" }}>✕</button>
              </div>
            ))}
            </div>
          </div>

          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Discount</label>
              <input type="number" placeholder="0.00" value={form.discount} min={0} step="0.01"
                onChange={e => { clearAiMark("discount"); setForm(f => ({ ...f, discount: e.target.value })); }}
                style={aiStyle("discount")} />
            </div>
            <div />
            <div>
              <label style={labelStyle}>VAT Rate (%)</label>
              <input type="number" value={form.vat_rate} onChange={e => { clearAiMark("vat_rate"); setForm(f => ({ ...f, vat_rate: e.target.value })); }} style={aiStyle("vat_rate")} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569" }}>
                <input type="checkbox" checked={form.is_vat_claimable} onChange={e => setForm(f => ({ ...f, is_vat_claimable: e.target.checked }))} />
                VAT is claimable as input tax credit
              </label>
            </div>
            <div>
              <label style={labelStyle}>WHT Category</label>
              <select value={form.wht_category} onChange={e => {
                const cat = WHT_CATEGORIES.find(c => c.value === e.target.value);
                setForm(f => ({ ...f, wht_category: e.target.value, wht_rate: cat?.rate || "0" }));
              }} style={inputStyle}>
                <option value="">No WHT</option>
                {WHT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label} ({c.rate}%)</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>WHT Rate (%)</label>
              <input type="number" value={form.wht_rate} onChange={e => { clearAiMark("wht_rate"); setForm(f => ({ ...f, wht_rate: e.target.value })); }} style={aiStyle("wht_rate")} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569", fontWeight: 600, marginBottom: form.is_recurring ? 10 : 0 }}>
              <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(f => ({ ...f, is_recurring: e.target.checked }))} />
              Make this a recurring bill
            </label>
            {form.is_recurring && (
              <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Recurs</label>
                  <select value={form.recurrence_interval} onChange={e => setForm(f => ({ ...f, recurrence_interval: e.target.value }))} style={inputStyle}>
                    {RECURRENCE_INTERVALS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>End Date (optional)</label>
                  <DateInput value={form.recurrence_end_date} onChange={e => setForm(f => ({ ...f, recurrence_end_date: e.target.value }))} style={inputStyle} />
                </div>
              </div>
            )}
          </div>

          <div style={{ background: "#f8fafc", borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <div style={{ fontSize: 13, color: "#64748b" }}>Subtotal: <strong>{form.currency_symbol}{calcSubtotal().toFixed(2)}</strong></div>
              {calcDiscount() > 0 && (
                <div style={{ fontSize: 13, color: "#dc2626" }}>Discount: <strong>-{form.currency_symbol}{calcDiscount().toFixed(2)}</strong></div>
              )}
              <div style={{ fontSize: 13, color: "#64748b" }}>VAT ({form.vat_rate}%): <strong>{form.currency_symbol}{calcVat().toFixed(2)}</strong></div>
              <div style={{ fontSize: 15, color: "#1e3a8a", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                Total: {form.currency_symbol}{calcTotal().toFixed(2)}
              </div>
              {calcWht() > 0 && (
                <div style={{ fontSize: 13, color: "#dc2626" }}>WHT ({form.wht_rate}%): <strong>-{form.currency_symbol}{calcWht().toFixed(2)}</strong></div>
              )}
              <div style={{ fontSize: 16, color: "#0f766e", fontWeight: 700, marginTop: 4, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                Net Payable: {form.currency_symbol}{calcNetPayable().toFixed(2)}
              </div>
            </div>
          </div>

          <button onClick={handleSubmit}
            disabled={!form.vendor_id || !form.bill_date || form.line_items.some(i => !i.description || !i.amount) || createMut.isPending}
            style={primaryBtnStyle}>
            {createMut.isPending ? "Creating…" : "Create Bill (Draft)"}
          </button>
        </div>
      )}

      {isLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading bills…</div>}
      {isError && <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>Something went wrong loading vendor bills.</div>}

      {!isLoading && !isError && bills.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          {(search || vendorFilterId !== "" || dateFromFilter || dateToFilter || currencyFilter || statusFilter) ? (
            <>No vendor bills match these filters.</>
          ) : (
            <>No vendor bills yet.</>
          )}
        </div>
      )}

      {!isLoading && !isError && bills.length > 0 && (
        isMobile ? (
          <div style={{ display: "grid", gap: 10 }}>
            {bills.map(renderBillCard)}
          </div>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b" }}>Bill #</th>
                  <SortHeader column="vendor" label="Vendor" />
                  <SortHeader column="status" label="Status" />
                  <SortHeader column="net_payable" label="Net Payable" align="right" />
                  <SortHeader column="bill_date" label="Bill Date" />
                  <SortHeader column="due_date" label="Due Date" />
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bills.map(renderBillTableRow)}
              </tbody>
            </table>
          </div>
        )
      )}

      {!isLoading && !isError && totalCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {totalCount} bill{totalCount !== 1 ? "s" : ""} — page {currentPage} of {totalPages}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
              style={{ ...outlineBtnStyle, opacity: currentPage <= 1 ? 0.5 : 1, cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}>
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
              style={{ ...outlineBtnStyle, opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}>
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pay modal */}
      {payModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24, width: 380 }}>
            <h3 style={{ margin: "0 0 14px", color: NAVY }}>Mark Bill as Paid</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Payment Method *</label>
              <input value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}
                placeholder="e.g. Bank Transfer, Cheque" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Payment Reference *</label>
              <input value={payForm.payment_reference} onChange={e => setPayForm(f => ({ ...f, payment_reference: e.target.value }))}
                placeholder="e.g. transaction / cheque number" style={inputStyle} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setPayModal(null)} style={outlineBtnStyle}>Cancel</button>
              <button
                disabled={!payForm.payment_method.trim() || !payForm.payment_reference.trim() || payMut.isPending}
                onClick={() => payMut.mutate({ id: payModal.id, data: payForm })}
                style={primaryBtnStyle}>
                {payMut.isPending ? "Saving…" : "Confirm Paid"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VAT drilldown modal */}
      {showVatDrilldown && vatSummary && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          onClick={() => setShowVatDrilldown(false)}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", color: NAVY }}>Claimable Input VAT — {(vatSummary as any).period}</h3>
            <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 13 }}>
              {(vatSummary as any).claimable_count} bill{(vatSummary as any).claimable_count !== 1 ? "s" : ""} marked VAT-claimable (approved or paid only)
            </p>
            <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
              {(vatSummary as any).claimable_bills.map((b: any) => (
                <div key={b.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{b.bill_number}</div>
                    <div style={{ color: "#94a3b8" }}>{b.vendor_name} · {formatDate(b.bill_date)}</div>
                  </div>
                  <div style={{ fontWeight: 700, color: "#166534" }}>{b.currency_symbol}{(b.vat_amount || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowVatDrilldown(false)} style={outlineBtnStyle}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {viewingId !== null && viewBill && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}
          onClick={() => setViewingId(null)}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", color: NAVY }}>{(viewBill as any).bill_number}</h3>
            <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 13 }}>{(viewBill as any).vendor?.name}</p>
            <div style={{ display: "grid", gap: 6, fontSize: 13, color: "#475569", marginBottom: 16 }}>
              <div>Bill date: {(viewBill as any).bill_date}</div>
              {(viewBill as any).due_date && <div>Due date: {(viewBill as any).due_date}</div>}
              <div>Subtotal: {(viewBill as any).currency_symbol}{(viewBill as any).subtotal?.toFixed(2)}</div>
              {(viewBill as any).discount > 0 && (
                <div>Discount: -{(viewBill as any).currency_symbol}{(viewBill as any).discount?.toFixed(2)}</div>
              )}
              <div>VAT ({(viewBill as any).vat_rate}%): {(viewBill as any).currency_symbol}{(viewBill as any).vat_amount?.toFixed(2)}</div>
              {(viewBill as any).wht_amount > 0 && (
                <div>WHT ({(viewBill as any).wht_rate}%): -{(viewBill as any).currency_symbol}{(viewBill as any).wht_amount?.toFixed(2)}</div>
              )}
              <div style={{ fontWeight: 700, color: NAVY }}>Net Payable: {(viewBill as any).currency_symbol}{(viewBill as any).net_payable?.toFixed(2)}</div>
              {(viewBill as any).creator_name && <div>Created by: {(viewBill as any).creator_name}</div>}
              {(viewBill as any).approver_name && <div>Approved by: {(viewBill as any).approver_name}</div>}
              {(viewBill as any).payment_method && <div>Paid via: {(viewBill as any).payment_method} ({(viewBill as any).payment_reference})</div>}
              {(viewBill as any).is_recurring && (
                <div>Recurring: {(viewBill as any).recurrence_interval}{(viewBill as any).recurrence_next_date ? ` — next: ${formatDate((viewBill as any).recurrence_next_date)}` : ""}</div>
              )}
              {(viewBill as any).recurring_source_bill_id && (
                <div>Auto-generated from bill #{(viewBill as any).recurring_source_bill_id}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {(viewBill as any).has_receipt && (
                <AsyncButton onClick={() => handleDownloadReceipt((viewBill as any).id)} style={outlineBtnStyle}>Download Receipt</AsyncButton>
              )}
              {(viewBill as any).status === "paid" && (viewBill as any).wht_amount > 0 && (
                <AsyncButton onClick={() => handleDownloadWhtCertificate((viewBill as any).id)} style={outlineBtnStyle}>Download WHT Certificate</AsyncButton>
              )}
              <button onClick={() => setViewingId(null)} style={outlineBtnStyle}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "#1e3a8a",
  color: "white", border: "none", padding: "10px 20px",
  borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
